import { FC, useMemo } from "react";
import { ICPRevenueWinLossDistribution } from "../../../../apis/companySearch/icpDistribution";
import { getSegmentNameFromRange } from "../../../Workspace/View/MarketLandscape/utils";
import { hasWinLossData } from "../../utils";
import { GenericICPWinLossChart } from "./GenericICPWinLossChart";
import { DEFAULT_API_REVENUE_RANGES } from "../../../Workspace/View/constants";

interface ICPRevenueWinLossChartProps {
  /** Win/loss distribution data from API */
  data: ICPRevenueWinLossDistribution[];
  /** Height of the chart container */
  height?: string;
  /** Optional total count to display (overrides calculated total from data) */
  totalCount?: number;
  /** Whether the chart is loading */
  isLoading?: boolean;
}

/**
 * Component that displays win/loss revenue distribution chart with stacked columns
 * Each column shows win and loss segments that total 100%
 */
export const ICPRevenueWinLossChart: FC<ICPRevenueWinLossChartProps> = ({
  data,
  height = "350px",
  totalCount,
  isLoading = false,
}) => {
  const getCategoryName = useMemo(
    () => (item: ICPRevenueWinLossDistribution) => {
      const range = {
        min: item.revenueRangeMin || 0,
        max: item.revenueRangeMax || Number.POSITIVE_INFINITY,
      };
      return getSegmentNameFromRange(range, DEFAULT_API_REVENUE_RANGES);
    },
    []
  );

  const sortData = useMemo(
    () =>
      (a: ICPRevenueWinLossDistribution, b: ICPRevenueWinLossDistribution) => {
        const minA = a.revenueRangeMin || 0;
        const minB = b.revenueRangeMin || 0;
        return minA - minB; // Sort by minimum revenue value (ascending)
      },
    []
  );

  // Show skeleton when loading, or don't render if there's no meaningful win/loss data when not loading
  if (!isLoading && !hasWinLossData(data)) {
    return null;
  }

  return (
    <GenericICPWinLossChart
      data={data}
      getCategoryName={getCategoryName}
      height={height}
      totalCount={totalCount}
      sortData={sortData}
      segment="Revenue"
      isLoading={isLoading}
    />
  );
};
